<template>
    <div class="medium-management-page">
        <div class="settings-card">
            <h2 class="page-title">
                <Icon icon="heroicons:camera" width="24" height="24" />
                媒体模型记录管理
            </h2>

            <!-- 搜索区域 -->
            <div class="search-bar">
                <div class="search-left">
                    <el-input v-model="searchForm.medium_name" placeholder="请输入媒体接口名称" clearable size="large"
                        class="search-input" @keyup.enter="handleSearch">
                        <template #prefix>
                            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
                        </template>
                    </el-input>

                    <el-button type="primary" size="large" @click="handleSearch" class="search-btn">
                        <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
                        <span class="btn-text">搜索</span>
                    </el-button>
                </div>

                <el-button type="success" size="large" @click="openAddDialog" class="add-btn">
                    <Icon icon="heroicons:plus" width="18" height="18" />
                    <span class="btn-text">新增媒体配置</span>
                </el-button>
            </div>

            <!-- 表格包装器 -->
            <div class="table-wrapper">
                <el-table :data="mediumList" stripe v-loading="loading" class="medium-table"
                    :row-style="{ background: 'transparent' }" :cell-style="{ background: 'transparent' }"
                    :header-cell-style="{ background: 'rgba(157, 78, 221, 0.15)', color: '#fff' }">
                    <el-table-column prop="medium_name" label="接口名称" width="180" />
                    <el-table-column prop="medium_api" label="接口类型" width="100">
                        <template #default="{ row }">
                            <el-tag v-if="row.medium_api === 0" type="success">免费接口</el-tag>
                            <el-tag v-else-if="row.medium_api === 1" type="success">多米接口</el-tag>
                            <el-tag v-else type="info">其他接口</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column prop="medium_type" label="媒体类型" width="100">
                        <template #default="{ row }">
                            <el-tag v-if="row.medium_type === 1" type="warning">图片</el-tag>
                            <el-tag v-else-if="row.medium_type === 2" type="primary">视频</el-tag>
                            <el-tag v-else type="info">音乐</el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column prop="key_preview" label="密钥预览" width="130">
                        <template #default="{ row }">
                            <span class="key-preview">{{ row.key_preview }}******</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="medium_url" label="接口地址" min-width="200" show-overflow-tooltip />
                    <el-table-column prop="notes" label="备注" width="150" />
                    <el-table-column prop="create_time" label="创建时间" width="180" />
                    <el-table-column prop="update_time" label="更新时间" width="180" />

                    <el-table-column label="操作" width="200" fixed="right">
                        <template #default="scope">
                            <button class="action-btn edit-btn" @click="openEditDialog(scope.row)">
                                <Icon icon="heroicons:pencil-square" width="14" height="14" />
                                <span class="btn-text">编辑</span>
                            </button>
                            <button class="action-btn delete-btn" @click="handleDelete(scope.row.id)">
                                <Icon icon="heroicons:trash" width="14" height="14" />
                                <span class="btn-text">删除</span>
                            </button>
                        </template>
                    </el-table-column>
                </el-table>
            </div>

            <!-- 分页组件 -->
            <div class="pagination-container">
                <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                    :total="pagination.total" :page-sizes="[10, 20, 30, 50]" :layout="paginationLayout"
                    @size-change="fetchMediumList" @current-change="fetchMediumList" background />
            </div>
        </div>

        <!-- 新增/编辑对话框 -->
        <el-dialog v-model="dialogVisible" :title="dialogTitle" :width="dialogWidth" :close-on-click-modal="false"
            class="custom-dialog" :top="dialogTop">
            <el-form ref="mediumForm" :model="mediumForm" :label-width="formLabelWidth" :rules="formRules">
                <el-form-item label="接口类型" prop="medium_api">
                    <el-select v-model="mediumForm.medium_api" placeholder="选择接口类型" size="large" style="width: 100%"
                        @change="handleApiTypeChange">
                        <el-option label="免费接口" :value="0" />
                        <el-option label="多米接口" :value="1" />
                        <el-option label="其他接口" :value="2" />
                    </el-select>
                </el-form-item>

                <el-form-item label="媒体类型" prop="medium_type">
                    <el-select v-model="mediumForm.medium_type" placeholder="选择媒体类型" size="large" style="width: 100%"
                        @change="handleMediaTypeChange">
                        <el-option label="图片" :value="1" />
                        <el-option label="视频" :value="2" />
                        <el-option label="音乐" :value="3" />
                    </el-select>
                </el-form-item>

                <el-form-item label="接口名称" prop="medium_name">
                    <el-input v-model="mediumForm.medium_name" placeholder="请输入接口名称" size="large" />
                </el-form-item>

                <el-form-item label="接口地址" prop="medium_url" style="width: 100%">
                    <div class="api-url-container" style="width: 100%;">
                        <el-select v-model="urlPrefix" style="width: 120px; margin-right: 10px">
                            <el-option label="https://" value="https://" />
                            <el-option label="http://" value="http://" />
                        </el-select>
                        <el-input v-model="mediumForm.medium_url" placeholder="请输入媒体接口地址" size="large"
                            style="flex: 1" />
                        <el-tooltip content="查看文档" placement="top">
                            <el-link :href="apiDocs[mediumForm.medium_api]?.[mediumForm.medium_type]" target="_blank"
                                style="margin-left: 10px"
                                v-if="apiDocs[mediumForm.medium_api]?.[mediumForm.medium_type]">
                                <Icon icon="heroicons:document-text" width="18" height="18" />
                            </el-link>
                        </el-tooltip>
                    </div>
                </el-form-item>

                <el-form-item label="接口密钥" prop="medium_key">
                    <el-input v-model="mediumForm.medium_key" placeholder="请输入媒体接口密钥" size="large" show-password />
                </el-form-item>

                <el-form-item label="备注信息">
                    <el-input v-model="mediumForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
                </el-form-item>
            </el-form>

            <template #footer>
                <button class="dialog-btn cancel-btn" @click="dialogVisible = false">取消</button>
                <button class="dialog-btn confirm-btn" @click="submitMediumForm">确认</button>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { Icon } from '@iconify/vue';
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
    name: 'SystemMediumApi',
    components: { Icon },
    data() {
        const validateUrl = (rule, value, callback) => {
            if (!value) {
                callback(new Error('请输入接口地址'));
            } else if (!/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/i.test(value)) {
                callback(new Error('请输入有效的URL地址'));
            } else {
                callback();
            }
        };
        return {
            apiDocs: {
                1: { 1: "https://duomiapi.com/user/register?cps=D926rY9O", 2: "https://duomiapi.com/user/register?cps=D926rY9O", 3: "https://duomiapi.com/user/register?cps=D926rY9O" }
            },
            urlPrefix: 'https://',
            loading: false,
            dialogVisible: false,
            dialogTitle: '新增媒体配置',
            isEditMode: false,
            searchForm: { medium_name: '' },
            mediumForm: { id: null, medium_api: null, medium_type: null, medium_name: '', medium_url: '', medium_key: '', notes: '' },
            formRules: {
                medium_api: [{ required: true, message: '请选择接口类型', trigger: 'blur' }],
                medium_type: [{ required: true, message: '请选择媒体类型', trigger: 'blur' }],
                medium_name: [{ required: true, message: '请输入接口名称', trigger: 'blur' }],
                medium_url: [{ required: true, validator: validateUrl, trigger: 'blur' }],
                medium_key: [{ required: true, message: '请输入接口密钥', trigger: 'blur' }]
            },
            mediumList: [],
            pagination: { page: 1, pageSize: 10, total: 0 },
            screenWidth: window.innerWidth
        }
    },
    computed: {
        userInfo() { return JSON.parse(Cookies.get('user_info') || '{}') },
        paginationLayout() {
            if (this.screenWidth < 576) return 'prev, pager, next'
            if (this.screenWidth < 768) return 'total, prev, pager, next'
            return 'total, sizes, prev, pager, next, jumper'
        },
        dialogWidth() {
            if (this.screenWidth < 576) return '95%'
            if (this.screenWidth < 768) return '90%'
            return '700px'
        },
        dialogTop() {
            if (this.screenWidth < 576) return '5vh'
            return '10vh'
        },
        formLabelWidth() {
            if (this.screenWidth < 576) return '80px'
            return '120px'
        }
    },
    watch: {
        'mediumForm.medium_url': function (newVal) {
            if (newVal && !/^https?:\/\//.test(newVal) && this.urlPrefix) {
                this.mediumForm.medium_url = newVal.replace(/^(https?:\/\/)?/, '');
            }
        }
    },
    mounted() {
        this.fetchMediumList();
        window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleResize() { this.screenWidth = window.innerWidth },
        handleMediaTypeChange(value) {
            if (this.mediumForm.medium_api === 0) {
                switch (value) {
                    case 1: this.mediumForm.medium_name = "免费图片接口"; break;
                    case 2: this.mediumForm.medium_name = "免费视频接口"; break;
                    case 3: this.mediumForm.medium_name = "免费音乐接口"; break;
                }
            } else if (this.mediumForm.medium_api === 1) {
                switch (value) {
                    case 1: this.mediumForm.medium_url = "duomiapi.com"; break;
                    case 2: this.mediumForm.medium_url = "duomiapi.com"; break;
                    case 3: this.mediumForm.medium_url = "duomiapi.com"; break;
                }
            }
        },
        handleApiTypeChange(value) {
            if (value === 0) {
                this.mediumForm.medium_name = "免费图片接口";
                this.mediumForm.medium_url = this.getDomainWithoutProtocol(window.location.origin);
                this.mediumForm.medium_key = "123456";
            } else if (value === 1 && this.mediumForm.medium_type) {
                this.handleMediaTypeChange(this.mediumForm.medium_type);
            } else if (value !== 1) {
                this.mediumForm.medium_url = '';
                this.mediumForm.medium_key = '';
                this.mediumForm.medium_name = '';
            }
        },
        async fetchMediumList() {
            this.loading = true;
            try {
                const params = { action: this.searchForm.medium_name ? 'search' : 'list', store_id: this.userInfo.id, page: this.pagination.page, page_size: this.pagination.pageSize };
                if (this.searchForm.medium_name) params.medium_name = this.searchForm.medium_name;
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/medium_model_key.php`, params);
                if (data.data) { this.mediumList = data.data; this.pagination.total = data.pagination?.total || data.data.length; }
            } catch (error) { ElMessage.error('获取媒体配置失败'); }
            finally { this.loading = false; }
        },
        handleSearch() { this.pagination.page = 1; this.fetchMediumList(); },
        getDomainWithoutProtocol(url) { return url.replace(/^https?:\/\//, ''); },
        openAddDialog() {
            this.dialogTitle = '新增媒体配置'; this.isEditMode = false;
            this.mediumForm = { id: null, medium_api: 0, medium_type: 1, medium_name: '免费图片接口', medium_url: this.getDomainWithoutProtocol(window.location.origin), medium_key: '123456', notes: '' };
            this.urlPrefix = 'https://';
            this.dialogVisible = true;
        },
        openEditDialog(row) {
            this.dialogTitle = '编辑媒体配置'; this.isEditMode = true;
            this.mediumForm = { id: row.id, medium_api: row.medium_api, medium_type: row.medium_type, medium_name: row.medium_name, medium_url: row.medium_url, medium_key: row.medium_key, notes: row.notes };
            if (row.medium_url && /^https?:\/\//.test(row.medium_url)) {
                this.urlPrefix = row.medium_url.match(/^https?:\/\//)[0];
                this.mediumForm.medium_url = row.medium_url.replace(/^https?:\/\//, '');
            } else { this.urlPrefix = 'https://'; }
            this.dialogVisible = true;
        },
        submitMediumForm() {
            this.$refs.mediumForm.validate(async (valid) => {
                if (valid) {
                    try {
                        const fullUrl = this.urlPrefix + this.mediumForm.medium_url;
                        const action = this.isEditMode ? 'update' : 'add';
                        const payload = { ...this.mediumForm, medium_url: fullUrl, store_id: this.userInfo.id };
                        const { data } = await axios.post(`${config.BASE_URL}/shop/api/medium_model_key.php`, { action, ...payload });
                        if (data.success) { ElMessage.success(this.isEditMode ? '更新成功' : '添加成功'); this.dialogVisible = false; this.fetchMediumList(); }
                        else { ElMessage.error(data.error || '操作失败'); }
                    } catch (error) { ElMessage.error('操作失败'); }
                }
            });
        },
        async handleDelete(id) {
            try {
                await ElMessageBox.confirm('确定要删除该媒体配置吗？此操作不可恢复', '警告', { confirmButtonText: '确认删除', cancelButtonText: '取消', type: 'warning' });
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/medium_model_key.php`, { action: 'delete', id: id, store_id: this.userInfo.id });
                if (data.success) { ElMessage.success('删除成功'); this.fetchMediumList(); }
                else { ElMessage.error(data.error || '删除失败'); }
            } catch (error) { if (error !== 'cancel') ElMessage.error('删除失败'); }
        }
    }
}
</script>

<style scoped lang="scss">
.medium-management-page {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
}

.settings-card {
    background: rgba(18, 14, 33, 0.6);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    border: 1px solid rgba(157, 78, 221, 0.2);
    padding: 24px;
    width: 100%;
    box-sizing: border-box;

    @media (max-width: 768px) {
        padding: 16px;
    }

    @media (max-width: 576px) {
        padding: 12px;
    }
}

.page-title {
    font-size: 20px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 10px;

    svg {
        color: #c084fc;
    }

    @media (max-width: 768px) {
        font-size: 18px;
        margin-bottom: 16px;
    }
}

.search-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 20px;

    .search-left {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-wrap: wrap;
        flex: 1;
    }

    .search-input {
        flex: 1;
        min-width: 200px;
    }

    @media (max-width: 768px) {
        flex-direction: column;

        .search-left {
            width: 100%;
        }

        .search-left .el-input {
            width: 100% !important;
        }

        .search-left .search-btn {
            width: 100%;
        }

        .add-btn {
            width: 100%;
        }
    }
}

.table-wrapper {
    position: relative;
    width: 100%;
    overflow-x: auto;
    margin-top: 20px;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        background: rgba(157, 78, 221, 0.1);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(157, 78, 221, 0.4);
        border-radius: 4px;

        &:hover {
            background: rgba(157, 78, 221, 0.6);
        }
    }
}

:deep(.el-table) {
    --el-table-bg-color: transparent !important;
    --el-table-tr-bg-color: transparent !important;
    --el-table-header-bg-color: transparent !important;
    --el-table-row-hover-bg-color: rgba(157, 78, 221, 0.1) !important;
    --el-table-current-row-bg-color: rgba(157, 78, 221, 0.15) !important;
    --el-table-stripe-row-bg-color: rgba(157, 78, 221, 0.05) !important;
    --el-table-border-color: rgba(157, 78, 221, 0.2) !important;
    background-color: transparent !important;
    color: #e0e0e0;

    &::before {
        display: none;
    }

    .el-table__header-wrapper {
        background-color: transparent !important;

        tr {
            background-color: transparent !important;

            th:not(.el-table-fixed-column--right) {
                background: rgba(157, 78, 221, 0.2) !important;
                color: #fff !important;
                font-weight: 600;
                border-bottom: 1px solid rgba(157, 78, 221, 0.3) !important;

                .cell {
                    color: #fff !important;
                }
            }
        }
    }

    .el-table__body-wrapper {
        background-color: transparent !important;

        tr {
            background-color: transparent !important;

            td:not(.el-table-fixed-column--right) {
                background-color: transparent !important;
                border-bottom: 1px solid rgba(157, 78, 221, 0.1);
                color: #e0e0e0;
            }

            &.el-table__row--striped td:not(.el-table-fixed-column--right) {
                background-color: rgba(157, 78, 221, 0.05) !important;
            }

            &:hover td:not(.el-table-fixed-column--right) {
                background-color: rgba(157, 78, 221, 0.1) !important;
            }
        }
    }

    .el-table-fixed-column--right {
        background-color: #120e21 !important;
        backdrop-filter: none !important;
    }

    th.el-table-fixed-column--right {
        background-color: #2a1f45 !important;
    }

    .el-table__row--striped .el-table-fixed-column--right {
        background-color: #1a1430 !important;
    }

    .el-table__empty-block {
        background-color: transparent !important;

        .el-table__empty-text {
            color: #a0a0c0 !important;
        }
    }
}

.key-preview {
    font-family: monospace;
    background: rgba(157, 78, 221, 0.15);
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 13px;
    color: #c084fc;
}

.action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    margin: 0 4px;

    &.edit-btn {
        background: rgba(157, 78, 221, 0.15);
        border: 1px solid rgba(157, 78, 221, 0.4);
        color: #c084fc;

        &:hover {
            background: rgba(157, 78, 221, 0.25);
        }
    }

    &.delete-btn {
        background: rgba(239, 68, 68, 0.15);
        border: 1px solid rgba(239, 68, 68, 0.4);
        color: #f87171;

        &:hover {
            background: rgba(239, 68, 68, 0.25);
        }
    }

    @media (max-width: 576px) {
        .btn-text {
            display: none;
        }
    }
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;

    @media (max-width: 768px) {
        justify-content: center;
    }
}

:deep(.el-pagination) {
    --el-pagination-bg-color: transparent;
    --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
    --el-pagination-hover-color: #c084fc;

    .btn-prev,
    .btn-next,
    .el-pager li {
        background: rgba(157, 78, 221, 0.1);
        border: 1px solid rgba(157, 78, 221, 0.3);
        color: #a0a0c0;
        border-radius: 8px;
        min-width: 32px;
        height: 32px;

        &:hover {
            background: rgba(157, 78, 221, 0.2);
            color: #c084fc;
        }

        &.active {
            background: linear-gradient(135deg, #6546b4, #9d4edd);
            color: #fff;
            border-color: transparent;
        }
    }

    .el-pagination__total,
    .el-pagination__jump {
        color: #a0a0c0;
    }
}

:deep(.custom-dialog) {
    --el-dialog-bg-color: rgba(18, 14, 33, 0.98);
    --el-dialog-border-radius: 20px;

    .el-dialog__header {
        border-bottom: 1px solid rgba(157, 78, 221, 0.2);
        padding: 20px 24px;

        .el-dialog__title {
            color: #fff;
            font-weight: 600;
        }
    }

    .el-dialog__body {
        padding: 24px;
    }

    .el-dialog__footer {
        border-top: 1px solid rgba(157, 78, 221, 0.2);
        padding: 16px 24px;
    }
}

:deep(.el-form-item__label) {
    color: #a0a0c0;
    font-weight: 500;
}

:deep(.el-input__wrapper) {
    background: rgba(26, 21, 53, 0.8);
    border: 1px solid rgba(157, 78, 221, 0.3);
    border-radius: 10px;
    box-shadow: none;

    &:hover {
        border-color: rgba(157, 78, 221, 0.6);
    }

    &.is-focus {
        border-color: #9d4edd;
        box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
    }

    .el-input__inner {
        color: #fff;
        background: transparent;

        &::placeholder {
            color: #5a5a7a;
        }
    }
}

:deep(.el-textarea__inner) {
    background: rgba(26, 21, 53, 0.8);
    border: 1px solid rgba(157, 78, 221, 0.3);
    border-radius: 10px;
    color: #fff;

    &:hover {
        border-color: rgba(157, 78, 221, 0.6);
    }

    &:focus {
        border-color: #9d4edd;
        box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
    }
}

.api-url-container {
    display: flex;
    align-items: center;
    width: 100%;
}

.dialog-btn {
    padding: 10px 20px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    margin-left: 12px;

    &.cancel-btn {
        background: rgba(157, 78, 221, 0.15);
        border: 1px solid rgba(157, 78, 221, 0.4);
        color: #a0a0c0;

        &:hover {
            background: rgba(157, 78, 221, 0.25);
            color: #c084fc;
        }
    }

    &.confirm-btn {
        background: linear-gradient(135deg, #6546b4, #9d4edd);
        color: #fff;

        &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
        }
    }
}

:deep(.el-button) {
    border-radius: 10px;
    padding: 10px 20px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
}

:deep(.el-button--primary) {
    background: linear-gradient(135deg, #6546b4, #9d4edd);
    border: none;

    &:hover {
        transform: translateY(-1px);
    }
}

:deep(.el-button--success) {
    background: linear-gradient(135deg, #10b981, #34d399);
    border: none;

    &:hover {
        transform: translateY(-1px);
    }
}

@media (max-width: 480px) {
    .btn-text {
        display: none;
    }
}
</style>

<!-- 非 scoped 全局样式 -->
<style lang="scss">
.el-dialog {
    --el-dialog-bg-color: rgba(18, 14, 33, 0.98) !important;
}

body>.el-overlay:has(.custom-dialog) .el-dialog__body {
    background: rgba(18, 14, 33, 0.98) !important;
}
</style>